/**
 * The ABN, in one place.
 *
 * The checksum existed three times in this repo — once in `buyerService`, once in the
 * seller-invoice reader on the API side, once inside the ABR lookup endpoint. Winnie's
 * 13 Sep note asks the Cost of Sales reader to validate an ABN too and says plainly:
 * "reuse the same 11-digit ABN checksum algorithm already specified in
 * Sale_Details_Workspace_Build_Spec.md section 3 — don't implement a second one."
 *
 * So this is the frontend's single copy. `buyerService` re-exports it under the name the
 * rest of the app already imports, and nothing here touches the network or the database:
 * it is text in, answer out, which is what lets the invoice parser use it.
 */

export const ABN_RE = /^\d{11}$/

/**
 * The ABN checksum, worth doing even without a registry call: it catches a transposed
 * digit, which a length check never will. https://abr.business.gov.au/Help/AbnFormat
 */
export function isValidAbn(abn: string): boolean {
  const digits = (abn || '').replace(/\s/g, '')
  if (!ABN_RE.test(digits)) return false
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19]
  const nums = digits.split('').map(Number)
  nums[0] -= 1
  const sum = nums.reduce((acc, n, i) => acc + n * weights[i], 0)
  return sum % 89 === 0
}

/** 11 digits, however the invoice spaced them: "12 345 678 901", "12345678901". */
const ABN_SHAPE = /\b\d{2}[ ]?\d{3}[ ]?\d{3}[ ]?\d{3}\b/g

/**
 * The supplier's ABN as printed on an invoice, or ''.
 *
 * Two rules, both deliberate. A candidate has to pass the checksum — an eleven-digit run
 * on an invoice is as likely to be a phone number, a BSB and account pair or a consignment
 * number as an ABN, and Winnie's note is explicit that a failed checksum means record
 * nothing rather than record something wrong: "don't record a bad ABN just because
 * something was found on the page."
 *
 * And where the page carries more than one valid ABN — most tax invoices carry the
 * buyer's as well as the seller's — a line that labels itself ABN wins, and Direct Auto's
 * own ABN is never returned as the supplier's.
 */
export function extractAbnFromText(text: string, ownAbn?: string | null): string {
  const raw = String(text || '')
  if (!raw) return ''
  const own = String(ownAbn || '').replace(/\D/g, '')

  const candidates = (line: string): string[] =>
    (line.match(ABN_SHAPE) || [])
      .map((m) => m.replace(/\s/g, ''))
      .filter((d) => isValidAbn(d) && d !== own)

  const lines = raw.split('\n')

  // A line that says ABN is the strongest evidence on the page.
  for (const line of lines) {
    if (!/\bA\.?B\.?N\.?\b/i.test(line)) continue
    const hit = candidates(line)
    if (hit.length) return hit[0]
  }

  // Otherwise the first valid ABN in the header block — the supplier's letterhead sits
  // above the bill-to block on every invoice template we have seen.
  for (const line of lines.slice(0, 25)) {
    const hit = candidates(line)
    if (hit.length) return hit[0]
  }

  return ''
}

/** "12 345 678 901" — how an ABN is written on a document. */
export const formatAbn = (abn: string): string => {
  const d = String(abn || '').replace(/\D/g, '')
  if (d.length !== 11) return String(abn || '')
  return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8, 11)}`
}
